import { TanStackDevtools } from '@tanstack/react-devtools';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, HeadContent, Scripts } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';

import NotFound from '~ai-tutor/components/NotFound';

import { ErrorBoundary } from '../components/ErrorBoundary';
import Footer from '../components/Footer';
import { FullStorySetup } from '../components/FullStorySetup';
import Header from '../components/Header';
import { Toaster } from '../components/ui/sonner';
import { UsercentricsSetup } from '../components/UsercentricsSetup';
import type { SessionData } from '../lib/auth/session';
import { AuthProvider } from '../lib/contexts/auth';
import type { UserEventAccess } from '../lib/ddb/userEventAccess';
import { useTheme } from '../lib/useTheme';
import appCss from '../styles.css?url';

export type MyRouterContext = {
  queryClient: QueryClient;
  session?: SessionData;
  conversationId?: string;
  oidcUser?: { userId: string; email: string; name?: string };
  events?: UserEventAccess[];
};

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'KI-Lernassistent',
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
    ],
  }),

  notFoundComponent: NotFound,

  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  useTheme();

  return (
    <html lang="de" className="h-full">
      <head>
        <HeadContent />
      </head>
      <body className="flex min-h-full flex-col">
        <AuthProvider>
          <UsercentricsSetup />
          <FullStorySetup />
          <ErrorBoundary>
            <Header />
            <main className="container mx-auto flex flex-1 flex-col px-4 pt-8 pb-8">{children}</main>
            <Toaster />
            <TanStackDevtools
              config={{
                position: 'bottom-left',
              }}
              plugins={[
                {
                  name: 'Tanstack Router',
                  render: <TanStackRouterDevtoolsPanel />,
                },
                {
                  name: 'Tanstack Query',
                  render: <ReactQueryDevtoolsPanel />,
                },
              ]}
            />
            <Footer />
          </ErrorBoundary>
        </AuthProvider>
        <Scripts />
      </body>
    </html>
  );
}
